'use client';

import { useEffect } from 'react';

import type { LOGIN_TYPE } from '@/api';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useDeviceInfoStore } from '@/stores/DeviceStore';

import { useMountAppData } from '../_hooks/useMountAppData';
import { dispatchAppEvent } from '../_utils/appEventBus';
import {
  type AppBridgeMessage,
  MESSAGE_KEYWORD,
  type MessageEvent,
  type MessageResponse,
} from '../_utils/reserved-keywords';
import type { FlutterInAppWebViewWindow } from '../_utils/window-types';

const isAppEventMessage = (
  message: AppBridgeMessage,
): message is MessageEvent =>
  message.type === MESSAGE_KEYWORD.ADPOPCORN_SDK_EVENT;

export const AppController = () => {
  const setAppMessageLog = useDeviceInfoStore(
    (state) => state.setAppMessageLog,
  );

  const router = useCustomRouter();

  useEffect(() => {
    const _window = window as FlutterInAppWebViewWindow;

    _window.router = {
      ...router,
      back: () => {},
    };

    const { setAppModal } = useDeviceInfoStore.getState();

    //* 🌱 앱에서 웹의 모달을 열때 사용할 메소드를 할당해줌
    _window.openAppModal = (type) => {
      setAppModal({
        isOpened: true,
        type,
      });
    };

    _window.postMessage = (response: string | AppBridgeMessage) => {
      const message: AppBridgeMessage =
        typeof response === 'string' ? JSON.parse(response) : response;

      if (isAppEventMessage(message)) {
        dispatchAppEvent(message);

        return;
      }

      const responseObj: MessageResponse = message;

      if (responseObj.isSuccess) {
        switch (responseObj.type) {
          case MESSAGE_KEYWORD.LOGIN: {
            const { accessKey } = responseObj.data as {
              accessKey: string;
            };

            router.push(
              ROUTES.AUTH.withSearchParams({
                searchParams: {
                  'social-login-token': accessKey,
                  'login-type': responseObj.params?.loginType as LOGIN_TYPE,
                },
              }),
            );

            break;
          }
        }
      }

      setAppMessageLog((prev) => {
        prev.set(responseObj.id, responseObj);

        return prev;
      });
    };
  }, [router, setAppMessageLog]);

  useMountAppData();

  return <></>;
};
